import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, WarningCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Segmented, Confirm } from "@/components/common";
import NoteDialog from "@/components/NoteDialog";
import { api, money, fmtDate, errMsg, todayStr } from "@/lib/api";
import { useLoad, useQueryParam } from "@/lib/useLoad";

const KIND = { check: "Çek", promissory: "Senet" };
const STATUS = { portfolio: "Portföyde", collected: "Tahsil Edildi", paid: "Ödendi", bounced: "Karşılıksız", cancelled: "İptal" };
const TONE = { portfolio: "approved", collected: "collection", paid: "collection", bounced: "payment", cancelled: "cancelled" };

export default function Notes() {
  const [direction, setDirection] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("portfolio");
  useQueryParam("yon", setDirection);
  useQueryParam("tur", setKind);
  const [open, setOpen] = useState(false);
  const [rows, reload] = useLoad(() => api.get("/notes", { params: { direction: direction || undefined, kind: kind || undefined, status: status || undefined } }).then((r) => r.data), [direction, kind, status]);
  const list = rows || [];
  const t = todayStr();
  const sum = (fn) => list.filter(fn).reduce((s, n) => s + n.amount, 0);
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setSt = (n, st) => act(() => api.patch(`/notes/${n.id}/status`, { status: st }), `${n.no} · ${STATUS[st]}`);

  return (
    <div>
      <PageHeader eyebrow="Nakit Yönetimi" title="Çek / Senet">
        <Button className="rounded-[4px]" onClick={() => setOpen(true)} data-testid="new-note-button"><Plus /> Çek / Senet Ekle</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Alınan (Varlık)" value={money(sum((n) => n.direction === "received"))} tone="green" testid="notes-total-received" />
        <Kpi label="Verilen (Borç)" value={money(sum((n) => n.direction === "issued"))} tone="red" testid="notes-total-issued" delay={40} />
        <Kpi label="Vadesi Geçen" value={list.filter((n) => n.status === "portfolio" && n.due_date < t).length} sub="portföyde bekleyen evrak" tone="amber" testid="notes-overdue-count" delay={80} />
      </div>
      <div className="panel">
        <div className="flex flex-col gap-3 border-b border-border p-4 md:flex-row md:flex-wrap md:items-center">
          <Segmented value={direction} onChange={setDirection} prefix="notes-direction" options={[["", "Tümü"], ["received", "Alınan"], ["issued", "Verilen"]]} />
          <Segmented value={kind} onChange={setKind} prefix="notes-kind" options={[["", "Çek + Senet"], ["check", "Çek"], ["promissory", "Senet"]]} />
          <Segmented value={status} onChange={setStatus} prefix="notes-status" options={[["portfolio", "Portföyde"], ["collected", "Tahsil Edilen"], ["paid", "Ödenen"], ["bounced", "Karşılıksız"], ["", "Hepsi"]]} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="notes-table">
            <thead><tr><Th>No</Th><Th>Tür</Th><Th>Yön</Th><Th>Cari</Th><Th>Seri / Banka</Th><Th>Vade</Th><Th>Durum</Th><Th right>Tutar</Th><Th /></tr></thead>
            <tbody>
              {rows && list.length === 0 && <Empty cols={9} text="Kayıtlı çek/senet yok" />}
              {list.map((n) => {
                const overdue = n.status === "portfolio" && n.due_date < t;
                return (
                  <tr key={n.id} className="hover:bg-muted/40" data-testid={`note-row-${n.no}`}>
                    <Td className="font-num text-xs font-semibold">{n.no}</Td>
                    <Td>{KIND[n.kind]}</Td>
                    <Td><Tag tone={n.direction === "received" ? "sale" : "purchase"}>{n.direction === "received" ? "Alınan" : "Verilen"}</Tag></Td>
                    <Td><Link to={`/cariler/${n.account_id}`} className="font-medium hover:text-primary">{n.account_name}</Link></Td>
                    <Td className="text-muted-foreground">{[n.serial_no, n.bank].filter(Boolean).join(" · ") || "—"}</Td>
                    <Td className={`font-num text-xs ${overdue ? "font-bold text-red-600" : ""}`}>{fmtDate(n.due_date)}{overdue && " !"}</Td>
                    <Td><Tag tone={TONE[n.status]}>{STATUS[n.status]}</Tag></Td>
                    <Td right className={n.direction === "received" ? "text-emerald-700" : "text-red-600"}>{money(n.amount)}</Td>
                    <Td className="whitespace-nowrap text-right">
                      {n.status === "portfolio" && (<>
                        <Button size="sm" variant="outline" className="mr-1 rounded-[4px]" onClick={() => setSt(n, n.direction === "received" ? "collected" : "paid")} data-testid={`note-close-${n.no}`}>
                          <CheckCircle /> {n.direction === "received" ? "Tahsil Edildi" : "Ödendi"}
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-[4px] text-red-600" onClick={() => setSt(n, "bounced")} data-testid={`note-bounce-${n.no}`}><WarningCircle /> Karşılıksız</Button>
                      </>)}
                      {n.status === "bounced" && <Button size="sm" variant="ghost" className="rounded-[4px]" onClick={() => setSt(n, "portfolio")} data-testid={`note-restore-${n.no}`}><ArrowCounterClockwise /> Portföye Al</Button>}
                      <Confirm title="Kaydı sil" description={`${n.no} silinsin mi? Cari hareketi geri alınır.`} testid={`note-delete-confirm-${n.no}`}
                        onConfirm={() => act(() => api.delete(`/notes/${n.id}`), "Kayıt silindi")}
                        trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`note-delete-${n.no}`}><Trash /></Button>} />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <NoteDialog open={open} onOpenChange={setOpen} onSaved={reload} />
    </div>
  );
}
